import { Card, CardDescription, CardHeader, CardTitle } from '@repo/ui'
import { Link, createFileRoute, useLoaderData } from '@tanstack/react-router'
import { Folder } from 'lucide-react'
import type { Project } from '@/components/AppSidebar'
import { PageContent } from '@/components/PageContent'
import { PageHeader } from '@/components/PageHeader'

export const Route = createFileRoute('/_auth/$orgSlug/')({
  head: () => ({ meta: [{ title: 'Snowvault' }] }),
  component: RouteComponent
})

function ProjectCard({ project, orgSlug }: { project: Project; orgSlug: string }) {
  return (
    <Link to="/$orgSlug/$projectId" params={{ orgSlug, projectId: String(project.id) }}>
      <Card className="transition-colors hover:bg-accent/50">
        <CardHeader className="flex-row items-center gap-3">
          <Folder className="h-5 w-5 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <CardTitle className="truncate text-base">{project.name}</CardTitle>
            <CardDescription className="text-xs">
              Last updated {new Date(project.lastUpdated).toLocaleDateString()}
            </CardDescription>
          </div>
        </CardHeader>
      </Card>
    </Link>
  )
}

const RECENT_PROJECT_LIMIT = 6

function recentProjects(projects: Array<Project>): Array<Project> {
  return [...projects].sort((a, b) => b.lastUpdated - a.lastUpdated).slice(0, RECENT_PROJECT_LIMIT)
}

function RouteComponent() {
  const { orgSlug } = Route.useParams()
  const { myProjects, sharedProjects } = useLoaderData({ from: '/_auth/$orgSlug' })
  const hasProjects = myProjects.length > 0 || sharedProjects.length > 0
  const recentMyProjects = recentProjects(myProjects)
  const recentSharedProjects = recentProjects(sharedProjects)

  return (
    <>
      <PageHeader breadcrumbs={[{ label: 'Home' }]} />
      <PageContent>
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold">Welcome to Snowvault</h2>
            <p className="mt-1 text-muted-foreground">Select a workspace to get started.</p>
          </div>

          {!hasProjects && <p className="text-muted-foreground">No workspaces found. Create one from the sidebar.</p>}

          {recentMyProjects.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground">My Workspaces</h3>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {recentMyProjects.map((p) => (
                  <ProjectCard key={p.id} project={p} orgSlug={orgSlug} />
                ))}
              </div>
            </div>
          )}

          {recentSharedProjects.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground">Shared Workspaces</h3>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {recentSharedProjects.map((p) => (
                  <ProjectCard key={p.id} project={p} orgSlug={orgSlug} />
                ))}
              </div>
            </div>
          )}
        </div>
      </PageContent>
    </>
  )
}
